import { useState, useEffect } from "react";
import { Bell, TrendingUp, Heart, Loader2, Eye, ChevronLeft, ChevronRight } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import { useUserRole } from "@/hooks/useUserRole";
import { supabase } from "@/integrations/supabase/client";
import watchDemoVideo from "@/assets/watch-feature-demo.mov";

interface FollowSubscriptionModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  handle: string;
  initialSlide?: number;
}

const SLIDES = [
  {
    id: "watch",
    icon: Eye,
    tagline: "Watch List",
    title: "See what an influencer is watching before they act.",
    description: "Get early insight into which assets influencers are monitoring — before they go long or short.",
    video: watchDemoVideo,
  },
  {
    id: "follow",
    icon: Bell,
    tagline: "Follow Alerts",
    title: "Get notified on new calls",
    description: "Receive alerts when your followed influencers post a new trade signal.",
  },
  {
    id: "performance",
    icon: TrendingUp,
    tagline: "Performance Tracking",
    title: "Track performance over time",
    description: "See how your favorite analysts perform and spot emerging trends.",
  },
  {
    id: "watchlist",
    icon: Heart,
    tagline: "Your Watchlist",
    title: "Build your watchlist",
    description: "Follow as many influencers as you want and manage them in one place.",
  },
];

export default function FollowSubscriptionModal({
  open,
  onOpenChange,
  handle,
  initialSlide = 0,
}: FollowSubscriptionModalProps) {
  const { user } = useAuth();
  const { isPremium, loading: rolesLoading } = useUserRole();
  const [loading, setLoading] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(initialSlide);

  useEffect(() => {
    if (open) setCurrentSlide(initialSlide);
  }, [open, initialSlide]);

  useEffect(() => {
    if (user && !rolesLoading && isPremium) {
      onOpenChange(false);
    }
  }, [user, rolesLoading, isPremium, onOpenChange]);

  const handleSubscribe = async () => {
    setLoading(true);
    try {
      const headers: Record<string, string> = {
        "Content-Type": "application/json",
        Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY}`,
      };

      if (user) {
        const { data: sessionData } = await supabase.auth.getSession();
        if (sessionData.session?.access_token) {
          headers["Authorization"] = `Bearer ${sessionData.session.access_token}`;
        }
      }

      const resp = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/create-follow-checkout`,
        {
          method: "POST",
          headers,
          body: JSON.stringify({ handle: handle.replace(/^@/, "") }),
        }
      );

      if (!resp.ok) throw new Error("Failed to create checkout");
      const { url } = await resp.json();
      window.location.href = url;
    } catch (err) {
      console.error("Checkout error:", err);
      setLoading(false);
    }
  };

  const slide = SLIDES[currentSlide];
  const SlideIcon = slide.icon;

  const goNext = () => setCurrentSlide((p) => (p + 1) % SLIDES.length);
  const goPrev = () => setCurrentSlide((p) => (p - 1 + SLIDES.length) % SLIDES.length);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg bg-card border-border p-0 overflow-hidden">
        {/* Carousel */}
        <div className="relative">
          {/* Media area */}
          <div className="relative bg-secondary/50 overflow-hidden" style={{ minHeight: 220 }}>
            {slide.video ? (
              <video
                key={slide.id}
                src={slide.video}
                autoPlay
                loop
                muted
                playsInline
                className="w-full h-56 object-cover"
              />
            ) : (
              <div className="flex items-center justify-center h-56 bg-gradient-to-br from-primary/10 via-secondary to-accent/10">
                <div className="p-6 rounded-full bg-primary/10">
                  <SlideIcon className="w-12 h-12 text-primary" />
                </div>
              </div>
            )}

            {/* Nav arrows */}
            <button
              onClick={goPrev}
              className="absolute left-2 top-1/2 -translate-y-1/2 p-1.5 rounded-full bg-background/70 text-foreground hover:bg-background/90 transition-colors"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <button
              onClick={goNext}
              className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 rounded-full bg-background/70 text-foreground hover:bg-background/90 transition-colors"
            >
              <ChevronRight className="w-4 h-4" />
            </button>

            {/* Dots */}
            <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5">
              {SLIDES.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setCurrentSlide(i)}
                  className={`w-2 h-2 rounded-full transition-colors ${
                    i === currentSlide ? "bg-primary" : "bg-foreground/30"
                  }`}
                />
              ))}
            </div>
          </div>

          {/* Slide content */}
          <div className="px-6 pt-4 pb-2">
            <div className="flex items-center gap-2 mb-1">
              <SlideIcon className="w-4 h-4 text-primary" />
              <span className="text-xs font-mono uppercase tracking-wider text-primary">{slide.tagline}</span>
            </div>
            <h3 className="text-lg font-bold text-foreground mb-1">{slide.title}</h3>
            <p className="text-sm text-muted-foreground">{slide.description}</p>
          </div>
        </div>

        {/* Price + CTA */}
        <div className="px-6 pb-6 space-y-3">
          <div className="text-center py-3 border-y border-border">
            <span className="text-2xl font-bold text-foreground">$9.99</span>
            <span className="text-muted-foreground text-sm"> / month</span>
          </div>

          <Button
            onClick={handleSubscribe}
            disabled={loading}
            className="w-full gap-2"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
            Subscribe Now
          </Button>

          {!user && (
            <Button
              variant="ghost"
              onClick={() => {
                window.location.href = `/login?returnTo=${encodeURIComponent(window.location.pathname + window.location.search)}`;
              }}
              className="w-full gap-2 text-xs text-muted-foreground"
            >
              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
              </svg>
              Or sign in with X
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
